import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Crown, MapPin, Trophy, Vote, Sparkles } from "lucide-react";

const QUEEN_IMG =
  "https://images.unsplash.com/photo-1506863530036-1efeddceb993?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxnbGFtb3JvdXMlMjB3b21hbiUyMHBvcnRyYWl0JTIwY2luZW1hdGljfGVufDB8fHx8MTc4NjIzMjA2Nnww&ixlib=rb-4.1.0&q=85";

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="grain min-h-screen relative overflow-hidden">
      <Helmet>
        <title>CitysElite — Every city crowns one queen</title>
        <meta name="description" content="CitysElite is a location-based ranking community. Share your photos and reels, get voted on daily, and rise to become the queen of your city." />
        <link rel="canonical" href="https://cityselite.com/" />
      </Helmet>
      <div className="absolute top-0 right-0 w-[60vw] h-[60vw] rounded-full bg-[#d4af37]/5 blur-[120px] pointer-events-none" />

      <header className="relative z-10 max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="gold-text" size={24} strokeWidth={1.5} />
          <span className="font-display text-3xl tracking-tight">
            Citys<span className="gold-text">Elite</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="header-login-link"
            className="text-sm text-zinc-300 hover:text-white transition-colors px-4 py-2"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="header-join-link"
            className="text-sm bg-white text-black hover:bg-zinc-200 transition-colors px-5 py-2 rounded-full font-medium"
          >
            Join
          </Link>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overline text-zinc-500 mb-6"
          >
            <MapPin size={12} className="inline mr-2 -mt-1" />
            Every city crowns one queen
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="free-entry-badge"
            className="inline-flex items-center gap-2 mb-6 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-1.5 text-xs font-medium gold-text"
          >
            <Sparkles size={13} strokeWidth={1.8} />
            Free entry for a limited time — no fee to compete
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
          >
            Rise through the ranks of your{" "}
            <span className="gold-text italic">city</span>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 text-zinc-400 text-lg max-w-md leading-relaxed"
          >
            Women share their reels and photos. Voters get 5 votes a day in
            each city. The most-loved becomes the queen of her location.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid="cta-post-btn"
              onClick={() => navigate("/register")}
              className="group flex items-center gap-2 bg-[#d4af37] text-black px-7 py-3.5 rounded-full font-medium hover:bg-[#e6c766] transition-colors gold-glow"
            >
              <Sparkles size={18} strokeWidth={1.5} />
              Post & compete
            </button>
            <button
              data-testid="cta-vote-btn"
              onClick={() => navigate("/register")}
              className="flex items-center gap-2 border border-white/15 px-7 py-3.5 rounded-full font-medium hover:bg-white/5 transition-colors"
            >
              <Vote size={18} strokeWidth={1.5} />
              Start voting
            </button>
          </motion.div>

          <div className="mt-14 flex items-center gap-8 text-zinc-500">
            <div>
              <div className="font-display text-3xl text-white">50</div>
              <div className="overline mt-1">Top ranks</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <div className="font-display text-3xl text-white">5/day</div>
              <div className="overline mt-1">Votes per city</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <div className="font-display text-3xl gold-text">1</div>
              <div className="overline mt-1">Queen</div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[3/4]">
            <img src={QUEEN_IMG} alt="Isabella, crowned queen of Miami on CitysElite" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
            <div className="absolute top-5 left-5 flex items-center gap-2 backdrop-blur-md bg-black/40 border border-[#d4af37]/40 rounded-full px-4 py-1.5">
              <Crown className="gold-text" size={15} strokeWidth={1.5} />
              <span className="overline gold-text">Queen · Miami</span>
            </div>
            <div className="absolute bottom-6 left-6">
              <div className="font-display text-4xl">Isabella</div>
              <div className="flex items-center gap-2 text-zinc-300 mt-1">
                <Trophy size={14} className="gold-text" strokeWidth={1.5} />
                <span className="text-sm">12,480 votes</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <footer className="relative z-10 max-w-6xl mx-auto px-6 py-10 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-sm text-zinc-500">
        <span>© {new Date().getFullYear()} CitysElite · 18+ only</span>
        <div className="flex items-center gap-6">
          <Link to="/cities/all" className="hover:text-white transition-colors" data-testid="footer-cities-link">Browse cities</Link>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
